/*
 * Which country's retailer links a reader sees.
 *
 * Three signals, in order of authority:
 *
 *   1. The reader's own choice, from the toggle. Wins over detection, and is
 *      remembered for 90 days rather than forever.
 *   2. Shopify's /browsing_context_suggestions.json, which resolves the country
 *      from the IP on Shopify's side. Same origin, no API key, no third party,
 *      and the visitor's IP never leaves Shopify. Roughly 95-99% right at country
 *      level, which is the bar. Officially documented under Markets / Localization
 *      Discovery, so it is a supported endpoint rather than a back door.
 *   3. The browser timezone, used only as the instant first paint while (2) is in
 *      flight, and as the fallback when (2) cannot be reached.
 *
 * Timezone is deliberately NOT the real signal. Firefox with
 * privacy.resistFingerprinting, Tor Browser, and timezone-spoofing extensions all
 * force the zone to UTC precisely to defeat this technique, and a US reader in
 * that group would silently fall back to Canada and never see a link they could
 * buy from. It is a good guess for the first 200ms and a bad basis for a decision.
 *
 * The resolved country is cached so every page after the first is instant.
 */
(function () {
  var CHOICE_KEY = 'gear-region';      // the reader picked this. Kept for CHOICE_TTL.
  var GEO_KEY = 'gear-region-geo';     // what Shopify last told us. Refreshable.
  var GEO_TTL = 7 * 24 * 60 * 60 * 1000;
  var CHOICE_TTL = 90 * 24 * 60 * 60 * 1000;
  var DEFAULT_REGION = 'ca';

  /* Canadian zones, for the first-paint guess only. America/Toronto and
     America/New_York are distinct strings even though both are Eastern, which is
     what makes this workable at the border. */
  var CA_ZONES = [
    'America/Atikokan', 'America/Blanc-Sablon', 'America/Cambridge_Bay',
    'America/Creston', 'America/Dawson', 'America/Dawson_Creek',
    'America/Edmonton', 'America/Fort_Nelson', 'America/Glace_Bay',
    'America/Goose_Bay', 'America/Halifax', 'America/Inuvik',
    'America/Iqaluit', 'America/Moncton', 'America/Montreal',
    'America/Nipigon', 'America/Rainy_River', 'America/Rankin_Inlet',
    'America/Regina', 'America/Resolute', 'America/St_Johns',
    'America/Swift_Current', 'America/Thunder_Bay', 'America/Toronto',
    'America/Vancouver', 'America/Whitehorse', 'America/Winnipeg',
    'America/Yellowknife'
  ];

  var LABELS = {
    ca: { current: 'Showing Canadian retailers.', other: 'Shopping from the US?' },
    us: { current: 'Showing US retailers.', other: 'Shopping from Canada?' }
  };

  function read(key) {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }

  function write(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      /* Private mode. The value still applies for this page view. */
    }
  }

  function validRegion(v) {
    return v === 'ca' || v === 'us' ? v : null;
  }

  /* The reader's own choice, stored as "region:timestamp".
   *
   * It used to be stored bare, as "us" or "ca", and honoured forever with no
   * expiry and no reset. One stray click on the toggle pinned someone to the
   * wrong country permanently, and there was no way back except finding the
   * toggle again and knowing what it meant. Values in the old bare format are
   * therefore treated as absent and cleared, so every profile carrying one
   * re-resolves once and heals itself.
   *
   * A deliberate choice still wins over geo detection, but only for 90 days.
   * People move, and a choice made once should not outlive the trip that
   * prompted it.
   */
  function chosen() {
    var raw = read(CHOICE_KEY);
    if (!raw) return null;
    var parts = raw.split(':');
    var region = validRegion(parts[0]);
    var at = parseInt(parts[1], 10);
    if (!region || !at || Date.now() - at > CHOICE_TTL) {
      try {
        localStorage.removeItem(CHOICE_KEY);
      } catch (e) {
        /* Private mode. Nothing to clear. */
      }
      return null;
    }
    return region;
  }

  function cachedGeo() {
    var raw = read(GEO_KEY);
    if (!raw) return null;
    var parts = raw.split(':');
    var region = validRegion(parts[0]);
    var at = parseInt(parts[1], 10);
    if (!region || !at || Date.now() - at > GEO_TTL) return null;
    return region;
  }

  function guessFromTimezone() {
    var zone = '';
    try {
      zone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    } catch (e) {
      return DEFAULT_REGION;
    }
    if (CA_ZONES.indexOf(zone) !== -1) return 'ca';
    if (zone.indexOf('America/') === 0 || zone === 'Pacific/Honolulu') return 'us';
    return DEFAULT_REGION;
  }

  /* Ask Shopify. Anything that is not the US is treated as Canada: those are the
     only two storefronts we can send a reader to, and Canada is ours. */
  function resolveCountry() {
    return fetch('/browsing_context_suggestions.json?country[enabled]=true',
                 { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var c = j && j.detected_values && j.detected_values.country;
        if (!c || !c.handle) return null;
        return c.handle === 'US' ? 'us' : 'ca';
      })
      .catch(function () { return null; });
  }

  /* A pick whose retailers are all in the other country would otherwise render a
     bare label with nothing after it. Counted from the data, not from layout. */
  function hideEmptyRows(region) {
    var rows = document.querySelectorAll('[data-gear-buy]');
    for (var i = 0; i < rows.length; i++) {
      var items = rows[i].querySelectorAll('[data-gear-region-item]');
      var shown = 0;
      for (var j = 0; j < items.length; j++) {
        var r = items[j].getAttribute('data-gear-region-item');
        if (r === 'all' || r === region) shown++;
      }
      rows[i].classList.toggle('gear-buy--empty', shown === 0);
    }
  }

  function apply(region) {
    document.documentElement.setAttribute('data-gear-region', region);
    hideEmptyRows(region);

    var toggle = document.querySelector('[data-gear-region-toggle]');
    if (!toggle) return;

    var other = region === 'ca' ? 'us' : 'ca';
    var current = toggle.querySelector('[data-gear-region-current]');
    var button = toggle.querySelector('[data-gear-region-switch]');

    if (current) current.textContent = LABELS[region].current;
    if (button) button.textContent = LABELS[region].other;

    /* Revealed only now: with no script this control would do nothing. */
    toggle.hidden = false;
    toggle.setAttribute('data-gear-region-next', other);
  }

  function init() {
    var choice = chosen();
    var cache = cachedGeo();

    /* First paint, synchronous and never blocking on a request. */
    apply(choice || cache || guessFromTimezone());

    /* Then revalidate against Shopify on EVERY load, not only when the cache is
       empty.

       This used to read `if (!choice && !cache)`, so once a country was cached
       it was believed for the full seven-day TTL and nothing could correct it.
       One wrong or unlucky answer stuck for a week. Latent rather than observed:
       the 2026-09-02 report of two Chrome profiles disagreeing turned out to be
       a deliberate toggle click that had saved permanently, not a stale cache.
       Both are fixed, but only the sticky choice was ever actually hit.

       Cache-then-revalidate instead: paint instantly from what we know, then
       correct within the same page view if Shopify disagrees. A choice the
       reader made with the toggle still wins and is never overwritten. */
    if (!choice) {
      resolveCountry().then(function (region) {
        if (!region) return;
        write(GEO_KEY, region + ':' + Date.now());
        if (!chosen() && region !== document.documentElement.getAttribute('data-gear-region')) {
          apply(region);
        }
      });
    }

    var button = document.querySelector('[data-gear-region-switch]');
    if (!button) return;

    button.addEventListener('click', function () {
      var toggle = button.closest('[data-gear-region-toggle]');
      var next = toggle ? toggle.getAttribute('data-gear-region-next') : DEFAULT_REGION;
      write(CHOICE_KEY, next + ':' + Date.now());
      apply(next);
    });
  }

  window.SWGearRegion = { apply: apply, init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
